import { PropsWithChildren, useCallback, useContext } from 'react';
import { CheckoutPageData } from 'src/modules/checkout/CheckoutPageMapping.ts';
import { calculateTotals } from 'src/modules/checkout/CalculateTotals.ts';
import { Price } from 'src/common/Price/Price';
import { SelectionDataContext } from 'src/projects/components/SelectionDataContext.ts';
import { ConfigContext } from 'src/configContext.ts';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { ValveRoutes } from 'src/router.tsx';
import { matchRoutes } from 'react-router-dom';
import { RouteObject } from 'react-router/dist/lib/context';
import { initLocationState, isLocationState } from 'src/modules/locationStateContext.ts';
import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import { Counter } from 'src/common/Counter/Counter.tsx';
import { updatedLocationState } from 'src/common/updatedLocationState.ts';
import { ExportTable } from 'shared/projects/ExportTable.ts';
import { removeByLabel, updateQtyByLabel } from 'src/projects/utils/updateQtyByLabel.ts';
import { Button } from 'src/common/Button/Button.tsx';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon.tsx';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';

type FetchValveSelectionProps = {
    searchParams: string;
    pathname: string;
    exportData: ExportTable;
    updateSelection: (searchParams: string, exportData: ExportTable) => void;
}

export const FetchValveSelection = ({
    searchParams,
    pathname,
    updateSelection,
    exportData,
    children,
}: PropsWithChildren<FetchValveSelectionProps>) => {
    const matches = matchRoutes<RouteObject>(ValveRoutes, pathname);

    if (matches === null) {
        throw new Error('match failed');
    }

    const [match] = matches;

    const { valveLabel, actuatorLabel } = match.params;

    const locationState = readSearchParam(
        new URLSearchParams(searchParams),
        LOCATION_STORE_PARAM_NAME,
        { ...initLocationState },
        isLocationState,
    );

    if (valveLabel !== undefined && locationState.valveLabel !== valveLabel) {
        locationState.valveLabel = valveLabel;
    }
    if (actuatorLabel !== undefined && locationState.actuatorLabel !== actuatorLabel) {
        locationState.actuatorLabel = actuatorLabel;
    }

    const { userSettings, currency } = useContext(ConfigContext);
    const ctx = useContext(SelectionDataContext);

    const data = (ctx?.data ?? undefined) as CheckoutPageData | undefined;

    const onAccessoryDelete = useCallback((label: string, pipeConnectors: boolean) => {
        updateSelection(
            updatedLocationState(
                new URLSearchParams(searchParams),
                {
                    ...locationState,
                    pipeConnectors,
                    selectedAccessories: locationState
                        .selectedAccessories
                        .filter((accessory) => accessory
                            .label !== label),
                },
            ).toString(),
            removeByLabel(exportData, label),
        );
    }, [exportData, locationState, searchParams, updateSelection]);

    const onAccessoryChange = useCallback((quantity: number, label: string, pipeConnectors: boolean) => {
        updateSelection(
            updatedLocationState(
                new URLSearchParams(searchParams),
                {
                    ...locationState,
                    pipeConnectors,
                    selectedAccessories: locationState
                        .selectedAccessories.map(
                            (accessory) => {
                                if (accessory.label === label) {
                                    return {
                                        ...accessory,
                                        qty: quantity,
                                    };
                                }
                                return accessory;
                            },
                        ),
                },
            ).toString(),
            updateQtyByLabel(
                exportData,
                label,
                quantity,
            ),
        );
    }, [exportData, locationState, searchParams, updateSelection]);

    if (ctx === null || ctx.loading || data === undefined) {
        return <SkeletonSelection/>;
    }

    const priceCurrency = ctx.currency;
    const { valve } = data.vssAccessories;

    const comboCurrent = locationState.combo;

    const comboCandidate = data.vssCombo
        .find((item) => item.Label.includes(comboCurrent && comboCurrent.mounted ? '+' : '/'));
    const combo = comboCandidate ?? valve;

    const { pricePerUnit } = calculateTotals(
        combo,
        data.vssAccessories,
        userSettings,
        {
            ...locationState,
            combo: comboCurrent ?? {
                valve: locationState.valveLabel,
                actuator: locationState.actuatorLabel,
                mounted: false,
                qty: 1,
            },
        },
        currency,
        locationState.pipeConnectors,
    );

    const { isRecommended } = parseRecommended(valve.connectors);

    const valveAccessories = prepareAccessories<
    AccessoriesPageData['vssAccessories']['valveAccessories']['0']
    >(
        [
            ...data.vssAccessories.valveAccessories,
            ...data.vssAccessories.actuatorAccessories || [],
        ],
        locationState.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessoriesBy(isRecommended)(a.accessory, b.accessory),
    );

    const pipeConnectors = valveAccessories
        .filter(({ accessory }) => isRecommended(accessory.item.Label))
        .map((connector) => ({ ...connector, qty: connector.qty }));

    const accessories = valveAccessories
        .filter(({ accessory }) => !isRecommended(accessory.item.Label))
        .map((connector) => ({ ...connector, qty: connector.qty }));

    return (
        <>
            <ProjectSelectionDeviceList
                title={<I18n messageId={MESSAGES.checkoutValveAndActuatorTitle}/>}
            >
                <ProjectSelectionDevice
                    productImage={(
                        <ProductImage
                            imgSrc={combo.image?.raw.join('') || ''}
                        >
                            <ValveIcon/>
                        </ProductImage>
                    )}
                    productLink={(
                        <ViewProductLink
                            userSettings={userSettings}
                            code={combo.Label}
                            content=""
                        >
                            <span className="vss-device__product-label">
                                {combo.Label}
                            </span>

                            <div className="vss-device__product-icon">
                                <ArrowRightIcon/>
                            </div>
                        </ViewProductLink>
                    )}
                    productDescription={(
                        <RenderHtml
                            data={combo.productType?.localized.join('') || ''}
                        />
                    )}
                    quantity={(
                        <Counter
                            size="sm"
                            value={comboCurrent?.qty || 1}
                            onChange={(qty) => {
                                updateSelection(
                                    updatedLocationState(
                                        new URLSearchParams(searchParams),
                                        {
                                            ...locationState,
                                            combo: {
                                                ...locationState.combo,
                                                qty,
                                            },
                                        },
                                    ).toString(),
                                    pipeConnectors.reduce(
                                        (acc, curr) => updateQtyByLabel(acc, curr.accessory.item.Label, curr.qty * qty),
                                        updateQtyByLabel(exportData, combo.Label, qty),
                                    ),
                                );
                            }}
                        />
                    )}
                    price={(
                        <Price
                            price={pricePerUnit}
                            currency={priceCurrency}
                            locale={userSettings.currentLocale}
                        />
                    )}
                />
            </ProjectSelectionDeviceList>
            {children}
            {pipeConnectors.length ? (
                <ProjectSelectionDeviceList
                    title={<I18n messageId={MESSAGES.PipeConnectors}/>}
                    actions={(
                        <Button
                            kind="ghost-secondary"
                            size="md"
                            icon={<DeleteIcon/>}
                            iconOnly={true}
                            onClick={() => {
                                updateSelection(
                                    updatedLocationState(
                                        new URLSearchParams(searchParams),
                                        {
                                            ...locationState,
                                            pipeConnectors: false,
                                            selectedAccessories: locationState
                                                .selectedAccessories
                                                .filter((accessory) => pipeConnectors
                                                    .find((connector) => accessory
                                                        .label === connector.accessory.item.Label) === undefined),
                                        },
                                    ).toString(),
                                    pipeConnectors.reduce(
                                        (acc, curr) => removeByLabel(acc, curr.accessory.item.Label),
                                        exportData,
                                    ),
                                );
                            }}
                        />
                    )}
                >
                    {pipeConnectors.map(({ accessory: { item }, qty, price }) => (
                        <ProjectSelectionDevice
                            key={item.Label}
                            productImage={(
                                <ProductImage
                                    imgSrc={item.image?.raw.join('') || ''}
                                >
                                    <ActuatorIcon/>
                                </ProductImage>
                            )}
                            productLink={(
                                <ViewProductLink
                                    userSettings={userSettings}
                                    code={item.Label}
                                    content=""
                                >
                                    {item.Label}

                                    <div className="vss-accessory__product-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                </ViewProductLink>
                            )}
                            productDescription={(
                                <RenderHtml
                                    data={item.accessoryType?.localized.join('') || ''}
                                />
                            )}
                            quantity={(
                                <Counter
                                    size="sm"
                                    value={qty * (locationState.pipeConnectors ? comboCurrent?.qty || 1 : 1)}
                                    onDelete={() => onAccessoryDelete(item.Label, false)}
                                    onChange={(
                                        quantity,
                                    ) => onAccessoryChange(quantity, item.Label, false)}
                                />
                            )}
                            price={(
                                <Price
                                    price={price}
                                    currency={priceCurrency}
                                    locale={userSettings.currentLocale}
                                />
                            )}
                        />

                    ))}
                </ProjectSelectionDeviceList>
            ) : null}

            {accessories.length ? (
                <ProjectSelectionDeviceList
                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                >
                    {accessories.map(({ accessory: { item }, qty, price }) => (
                        <ProjectSelectionDevice
                            key={item.Label}
                            productImage={(
                                <ProductImage
                                    imgSrc={item.image?.raw.join('') || ''}
                                >
                                    <ActuatorIcon/>
                                </ProductImage>
                            )}
                            productLink={(
                                <ViewProductLink
                                    userSettings={userSettings}
                                    code={item.Label}
                                    content=""
                                >
                                    {item.Label}

                                    <div className="vss-accessory__product-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                </ViewProductLink>
                            )}
                            productDescription={(
                                <RenderHtml
                                    data={item.accessoryType?.localized.join('') || ''}
                                />
                            )}
                            quantity={(
                                <Counter
                                    size="sm"
                                    value={qty}
                                    onDelete={() => onAccessoryDelete(item.Label, locationState.pipeConnectors)}
                                    onChange={(
                                        quantity,
                                    ) => onAccessoryChange(quantity, item.Label, locationState.pipeConnectors)}
                                />
                            )}
                            price={(
                                <Price
                                    price={price}
                                    currency={priceCurrency}
                                    locale={userSettings.currentLocale}
                                />
                            )}
                        />
                    ))}
                </ProjectSelectionDeviceList>
            ) : null}
        </>
    );
};
